import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Search, Loader2 } from "lucide-react";

export default function StockSearch({ onSelect }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const res = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
        setResults(res.data.results || []);
        setOpen(true);
        setActive(0);
      } catch (e) {} finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(id);
  }, [q]);

  const pick = (r) => {
    setQ("");
    setResults([]);
    setOpen(false);
    onSelect(r.code);
  };

  const onKey = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(results[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative w-full" data-testid="stock-search">
      <div className="relative">
        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          onKeyDown={onKey}
          placeholder="Cari 950+ saham IDX (kode atau nama, mis. BBCA / avia)"
          data-testid="stock-search-input"
          className="w-full h-14 pl-11 pr-11 rounded-xl bg-[var(--surface-card)] border border-[var(--border)] text-base outline-none focus:border-[var(--accent)] transition-colors font-mono-data uppercase placeholder:normal-case placeholder:font-sans placeholder:text-[var(--text-muted)]"
        />
        {loading && <Loader2 size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-accent animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-30 mt-2 w-full bg-[var(--surface-card)] border border-[var(--border)] rounded-xl shadow-2xl overflow-hidden max-h-80 overflow-y-auto" data-testid="search-results">
          {results.map((r, i) => (
            <button
              key={r.code}
              onClick={() => pick(r)}
              onMouseEnter={() => setActive(i)}
              data-testid={`search-result-${r.code}`}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${i === active ? "bg-[var(--surface-hover)]" : ""}`}
            >
              <span className="w-9 h-9 rounded-lg bg-[var(--accent)]/12 border border-[var(--accent)]/25 flex items-center justify-center text-xs font-bold text-accent shrink-0">{r.code.slice(0, 2)}</span>
              <div className="min-w-0">
                <p className="font-heading font-bold text-accent flex items-center gap-1.5">
                  {r.code}
                  {r.new && <span data-testid={`search-new-${r.code}`} className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[var(--gold)]/15 text-gold">Baru</span>}
                </p>
                <p className="text-xs text-[var(--text-secondary)] truncate">{r.name}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
